import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../auth/AuthContext";
import { useApi } from "../api/ApiContext";
import { useNavigate, Link } from "react-router-dom";
import {
  Box, Typography, Avatar, IconButton, Divider, CircularProgress,
  Modal, Snackbar, Alert,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import GridOnIcon from "@mui/icons-material/GridOn";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import EmailIcon from "@mui/icons-material/Email";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { motion, AnimatePresence } from "framer-motion";

function isVideoFile(p) { return /\.(mp4|webm|mov|avi|mkv|m4v)$/i.test(p || ""); }
function getThumbnailUrl(p) {
  if (!p) return "";
  const base = p.split("/").pop().replace(/\.[^.]+$/, "");
  return `https://ksiega.kalmarry.pl/uploads/thumbs/${base}.jpg`;
}
function getFileUrl(p) {
  if (!p) return "";
  return p.startsWith("http") ? p : `https://ksiega.kalmarry.pl/${p.replace(/^\/+/, "")}`;
}
function getFileExt(p) { const m = (p || "").match(/\.(\w+)(?:\?.*)?$/); return m ? m[1].toLowerCase() : "jpg"; }
async function downloadBlob(url, filename) {
  const resp = await fetch(url, { mode: "cors" });
  const blob = await resp.blob();
  const a = Object.assign(document.createElement("a"), { href: window.URL.createObjectURL(blob), download: filename });
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  window.URL.revokeObjectURL(a.href);
}

const slideVariants = {
  enter: (d) => ({ x: d > 0 ? 300 : -300, opacity: 0 }),
  center: { x: 0, opacity: 1, transition: { duration: 0.22 } },
  exit: (d) => ({ x: d > 0 ? -300 : 300, opacity: 0, transition: { duration: 0.12 } }),
};

function GridCell({ img, isLiked, onClick }) {
  const [loaded, setLoaded] = useState(false);
  const isVid = isVideoFile(img.image_path);
  const thumbUrl = getThumbnailUrl(img.image_path);

  return (
    <Box
      onClick={onClick}
      sx={{
        aspectRatio: "1/1", overflow: "hidden", position: "relative",
        bgcolor: loaded ? "#111" : "#e8e8e8", cursor: "pointer", contain: "layout style",
        transition: "background-color 0.2s",
      }}
    >
      {isVid ? (
        <img src={thumbUrl} alt="" loading="lazy"
          onLoad={() => setLoaded(true)}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", opacity: loaded ? 1 : 0, transition: "opacity 0.25s" }}
          onError={e => {
            const v = document.createElement("video");
            v.src = `${img.url}#t=0.001`; v.preload = "metadata"; v.muted = true; v.playsInline = true;
            v.onloadeddata = () => setLoaded(true);
            Object.assign(v.style, { width: "100%", height: "100%", objectFit: "cover", display: "block", opacity: "0", transition: "opacity 0.25s" });
            e.target.replaceWith(v);
          }}
        />
      ) : (
        <img src={thumbUrl} alt="" loading="lazy"
          onLoad={() => setLoaded(true)}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", opacity: loaded ? 1 : 0, transition: "opacity 0.25s" }}
          onError={e => { e.target.src = img.url; }}
        />
      )}
      {isVid && (
        <PlayArrowIcon sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", color: "#fff", fontSize: 26, opacity: loaded ? 1 : 0, transition: "opacity 0.25s", pointerEvents: "none" }} />
      )}
      {isLiked && (
        <FavoriteIcon sx={{ position: "absolute", top: 5, right: 5, fontSize: 13, color: "#EB578A", zIndex: 2 }} />
      )}
    </Box>
  );
}

export default function Profile() {
  const { user, logout } = useAuth();
  const api = useApi();
  const navigate = useNavigate();

  // ── All state/refs must come before any early return ──────────────────────
  const [images, setImages] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("grid");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalIdx, setModalIdx] = useState(0);
  const [direction, setDirection] = useState(0);
  const [toast, setToast] = useState({ open: false, msg: "", severity: "success" });
  const touchStartX = useRef(null);

  // Derived value computed unconditionally so hooks below can reference it
  const displayedImages = view === "liked"
    ? images.filter(i => favorites.includes(i.zdjecie_id))
    : images;

  const showToast = (msg, severity = "success") => setToast({ open: true, msg, severity });
  const openModal = (idx) => { setModalIdx(idx); setDirection(0); setModalOpen(true); };
  const showPrev = () => { if (modalIdx > 0) { setDirection(-1); setModalIdx(i => i - 1); } };
  const showNext = () => { if (modalIdx < displayedImages.length - 1) { setDirection(1); setModalIdx(i => i + 1); } };

  // ── All useEffects before any early return ────────────────────────────────
  useEffect(() => {
    if (!user) return;
    setLoading(true);
    api.get(`/get_user_images.php?user_id=${user.id}`)
      .then(res => {
        const imgs = (res.data || []).map(img => ({ ...img, url: getFileUrl(img.image_path) }));
        setImages(imgs);
        setFavorites(imgs.filter(i => i.liked === 1).map(i => i.zdjecie_id));
      })
      .finally(() => setLoading(false));
  }, [user?.id, api]); // eslint-disable-line

  useEffect(() => {
    document.body.style.overflow = modalOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [modalOpen]);

  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") showPrev();
      else if (e.key === "ArrowRight") showNext();
      else if (e.key === "Escape") setModalOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [modalOpen, modalIdx, displayedImages.length]); // eslint-disable-line

  // ── Early return after all hooks ──────────────────────────────────────────
  if (!user) return null;

  const initials = `${user.first_name?.[0] || ""}${user.last_name?.[0] || ""}`.toUpperCase();
  const handleLogout = () => { logout(); navigate("/login"); };
  const handleDownload = async (img) => {
    if (!img?.image_path) return;
    try { await downloadBlob(getFileUrl(img.image_path), `zdjecie_${img.zdjecie_id || Date.now()}.${getFileExt(img.image_path)}`); }
    catch { showToast("Nie udało się pobrać.", "error"); }
  };

  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (dx > 50) showPrev(); else if (dx < -50) showNext();
    touchStartX.current = null;
  };

  const currentImg = displayedImages[modalIdx];
  const currentIsVideo = isVideoFile(currentImg?.image_path);

  return (
    <Box sx={{ bgcolor: "#fff", minHeight: "100vh" }}>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>

        {/* ── Header ── */}
        <Box sx={{ px: 2, pt: 2.5, pb: 2, maxWidth: { md: 935 }, mx: "auto" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 3, mb: 2 }}>
            <Box sx={{ borderRadius: "50%", background: "linear-gradient(135deg, #EB578A, #C0145E, #FF9A3C)", p: "3px", flexShrink: 0 }}>
              <Avatar sx={{ width: 80, height: 80, bgcolor: "#1a1a2e", color: "#fff", fontSize: 28, fontWeight: 800, border: "3px solid #fff" }}>
                {initials}
              </Avatar>
            </Box>
            <Box sx={{ flex: 1, display: "flex", gap: 0 }}>
              {[
                { label: "zdjęcia", value: images.length },
                { label: "ulubione", value: favorites.length },
              ].map(stat => (
                <Box key={stat.label} sx={{ flex: 1, textAlign: "center" }}>
                  <Typography sx={{ fontWeight: 800, fontSize: 18, color: "#111", lineHeight: 1.2 }}>{stat.value}</Typography>
                  <Typography sx={{ fontSize: 12, color: "#888", fontWeight: 400 }}>{stat.label}</Typography>
                </Box>
              ))}
              <Box sx={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center" }}>
                <IconButton onClick={handleLogout}
                  sx={{ bgcolor: "#fff0f5", color: "#EB578A", border: "1.5px solid #ffd6e7", borderRadius: "10px", p: 1 }}>
                  <LogoutIcon />
                </IconButton>
              </Box>
            </Box>
          </Box>

          <Typography sx={{ fontWeight: 800, fontSize: 15, color: "#111", lineHeight: 1.2 }}>
            {user.first_name} {user.last_name}
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#888", mt: 0.3 }}>Gość · Księga Gości</Typography>

          <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
              <EmailIcon sx={{ fontSize: 14, color: "#aaa" }} />
              <Typography sx={{ fontSize: 13, color: "#555" }}>{user.email}</Typography>
            </Box>
            {user.expires_at && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                <CalendarTodayIcon sx={{ fontSize: 14, color: "#aaa" }} />
                <Typography sx={{ fontSize: 13, color: "#555" }}>
                  Konto ważne do: <Box component="span" sx={{ fontWeight: 600 }}>{user.expires_at}</Box>
                </Typography>
              </Box>
            )}
          </Box>

          {user.image_limit && (
            <Box sx={{ mt: 1.5, bgcolor: "#f8f8f8", borderRadius: 2, px: 1.5, py: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                <Typography sx={{ fontSize: 12, color: "#888" }}>Wykorzystany limit</Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#EB578A" }}>
                  {images.length} / {user.image_limit}
                </Typography>
              </Box>
              <Box sx={{ height: 4, bgcolor: "#efefef", borderRadius: 2, overflow: "hidden" }}>
                <Box sx={{
                  height: "100%", borderRadius: 2,
                  width: `${Math.min(100, (images.length / user.image_limit) * 100)}%`,
                  background: "linear-gradient(90deg, #EB578A, #C0145E)",
                  transition: "width 0.5s",
                }} />
              </Box>
            </Box>
          )}
        </Box>

        <Divider sx={{ borderColor: "#efefef" }} />

        {/* ── View toggle ── */}
        <Box sx={{ display: "flex", borderBottom: "1px solid #efefef", maxWidth: { md: 935 }, mx: "auto" }}>
          {[
            { key: "grid", icon: <GridOnIcon sx={{ fontSize: 22 }} /> },
            { key: "liked", icon: <FavoriteIcon sx={{ fontSize: 22 }} /> },
          ].map(tab => (
            <Box key={tab.key} onClick={() => setView(tab.key)}
              sx={{
                flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
                py: 1.2, cursor: "pointer",
                borderBottom: view === tab.key ? "2px solid #111" : "2px solid transparent",
                color: view === tab.key ? "#111" : "#aaa",
                transition: "color 0.15s",
              }}
            >
              {tab.icon}
            </Box>
          ))}
        </Box>

        {/* ── Photo grid ── */}
        <Box sx={{ maxWidth: { md: 935 }, mx: "auto" }}>
          {loading ? (
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "2px" }}>
              {Array.from({ length: 9 }).map((_, i) => (
                <Box key={i} sx={{ aspectRatio: "1/1", bgcolor: i % 2 === 0 ? "#ebebeb" : "#e2e2e2" }} />
              ))}
            </Box>
          ) : displayedImages.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <Typography sx={{ color: "#aaa", fontSize: 14 }}>
                {view === "liked" ? "Brak ulubionych zdjęć" : "Brak zdjęć"}
              </Typography>
              {view === "grid" && (
                <Box component={Link} to="/upload"
                  sx={{ display: "inline-block", mt: 2, color: "#EB578A", fontWeight: 700, fontSize: 14, textDecoration: "none" }}>
                  + Dodaj pierwsze zdjęcie
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "2px" }}>
              {displayedImages.map((img, idx) => (
                <GridCell
                  key={img.zdjecie_id}
                  img={img}
                  isLiked={favorites.includes(img.zdjecie_id)}
                  onClick={() => openModal(idx)}
                />
              ))}
            </Box>
          )}
        </Box>

      </motion.div>

      {/* ── Modal viewer ── */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Box
          sx={{ position: "fixed", inset: 0, bgcolor: "#000", zIndex: 1300 }}
          onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}
        >
          <IconButton onClick={() => setModalOpen(false)}
            sx={{ position: "fixed", top: "max(env(safe-area-inset-top),14px)", right: 14, zIndex: 20, color: "#fff", bgcolor: "rgba(0,0,0,0.45)", backdropFilter: "blur(8px)" }}>
            <CloseIcon />
          </IconButton>

          {modalIdx > 0 && (
            <Box onClick={e => { e.stopPropagation(); showPrev(); }}
              sx={{ position: "fixed", left: 16, top: "50%", transform: "translateY(-50%)", zIndex: 20, width: 44, height: 44, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.15)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff", fontSize: 24, userSelect: "none", "&:hover": { bgcolor: "rgba(255,255,255,0.25)" } }}>‹</Box>
          )}
          {modalIdx < displayedImages.length - 1 && (
            <Box onClick={e => { e.stopPropagation(); showNext(); }}
              sx={{ position: "fixed", right: 16, top: "50%", transform: "translateY(-50%)", zIndex: 20, width: 44, height: 44, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.15)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff", fontSize: 24, userSelect: "none", "&:hover": { bgcolor: "rgba(255,255,255,0.25)" } }}>›</Box>
          )}

          <AnimatePresence initial={false} custom={direction} mode="wait">
            <motion.div
              key={modalIdx}
              custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit"
              style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column" }}
            >
              <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                {currentImg && (currentIsVideo ? (
                  <video key={currentImg.url} src={currentImg.url} controls autoPlay playsInline
                    style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                ) : (
                  <img src={currentImg.url} alt=""
                    style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", display: "block" }}
                    draggable={false} />
                ))}
              </Box>

              <Box sx={{
                position: "absolute", bottom: 0, left: 0, right: 0,
                background: "linear-gradient(0deg, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.5) 60%, transparent 100%)",
                px: 2, pt: 5, pb: "calc(env(safe-area-inset-bottom) + 24px)",
              }}>
                {currentImg?.text && (
                  <Typography sx={{ color: "#fff", fontSize: 14, mb: 1.5, lineHeight: 1.4 }}>
                    {currentImg.text}
                  </Typography>
                )}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: 13 }}>
                    {modalIdx + 1} / {displayedImages.length}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <IconButton onClick={() => handleDownload(currentImg)}
                      sx={{ bgcolor: "rgba(255,255,255,0.15)", backdropFilter: "blur(8px)", color: "#fff" }}>
                      <DownloadIcon />
                    </IconButton>
                    {favorites.includes(currentImg?.zdjecie_id)
                      ? <IconButton sx={{ bgcolor: "rgba(235,87,138,0.3)", backdropFilter: "blur(8px)", color: "#EB578A" }}><FavoriteIcon /></IconButton>
                      : <IconButton sx={{ bgcolor: "rgba(255,255,255,0.15)", backdropFilter: "blur(8px)", color: "#fff" }}><FavoriteBorderIcon /></IconButton>
                    }
                  </Box>
                </Box>
              </Box>
            </motion.div>
          </AnimatePresence>
        </Box>
      </Modal>

      <Snackbar open={toast.open} autoHideDuration={2400} onClose={() => setToast(t => ({ ...t, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={toast.severity} onClose={() => setToast(t => ({ ...t, open: false }))} sx={{ borderRadius: 3, fontWeight: 600 }}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
